"use client";

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { Offer, ProductData } from "@/services/dataTypes";
import { FontAwesomeIcon, faTimes, faCopy, faCheck, faTag, faBagShopping, faChevronDown, faChevronUp } from "@/constants/icons";
import { discardHTMLTags, getBaseImageUrl } from "@/constants/hooks";
import OfferDuration from "./OfferDuration";

interface Props {
    data: Offer | ProductData;
    onClose: () => void;
    domain: string;
    merchantHref: string;
    finalDiscountTag?: string | null;
    merchantImg?: string | null;
    productImg?: string | null;
}

const SimpleOfferModal = ({ data, onClose, domain, merchantHref, finalDiscountTag, merchantImg, productImg }: Props) => {
    const [mounted, setMounted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [imageSrc, setImageSrc] = useState<string>('');
    const [showFullDesc, setShowFullDesc] = useState(false);

    useEffect(() => {
        setMounted(true);
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
            setMounted(false);
        };
    }, [onClose]);

    useEffect(() => {
        if (productImg) {
            setImageSrc(getBaseImageUrl(domain, productImg, ""));
        } else if ((data as any)?.offer_type?.name === "product" && (data as ProductData)?.product_image) {
            setImageSrc(getBaseImageUrl(domain, (data as ProductData)?.product_image, ""));
        } else if (merchantImg) {
            setImageSrc(getBaseImageUrl(domain, merchantImg, ""));
        } else if ((data as Offer).merchant?.merchant_logo) {
            setImageSrc(getBaseImageUrl(domain, (data as Offer).merchant.merchant_logo, ""));
        }
    }, [data, domain, merchantImg, productImg]);

    const handleCopy = () => {
        if (!data?.coupon_code) return;
        const performCopy = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(data.coupon_code!).then(performCopy).catch(() => fallbackCopy(data.coupon_code!, performCopy));
        } else {
            fallbackCopy(data.coupon_code!, performCopy);
        }
    };

    const fallbackCopy = (text: string, callback: () => void) => {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.cssText = "position:fixed;left:-9999px;top:0";
        document.body.appendChild(el);
        el.focus(); el.select();
        try { document.execCommand('copy'); callback(); } catch {}
        document.body.removeChild(el);
    };

    const finalUrl = data?.url?.startsWith('/') ? data?.url.replace(/^\/+/, '') : data?.url;
    const absoluteOutUrl = `/${finalUrl}`;
    const cleanDesc = discardHTMLTags(data?.offer_detail || "");
    const isLongDesc = cleanDesc.length > 150;

    if (!mounted) return null;

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[50000] flex items-center justify-center p-3 sm:p-4 bg-[#1E3A5F]/75 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-[#F8FAFC] text-[#4B5563] hover:bg-[#E5E7EB] hover:text-[#111827] transition-all"
                    aria-label="Close"
                >
                    <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                </button>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1 hide-scroll">

                    {/* ── Header ── */}
                    <div className="px-6 pt-8 pb-6 text-center border-b border-[#E5E7EB]">

                        {/* Logo */}
                        <div className="relative w-20 h-20 mx-auto mb-5 bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] p-3 flex items-center justify-center">
                            {finalDiscountTag && (
                                <span className="absolute -top-2.5 -right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#FEE2E2] text-[#DC2626] text-[10px] font-bold z-10">
                                    <FontAwesomeIcon icon={faTag} className="w-3 h-3" />
                                    {finalDiscountTag}
                                </span>
                            )}
                            <Image
                                src={imageSrc}
                                alt="offer"
                                width={64}
                                height={64}
                                className="object-contain"
                            />
                        </div>

                        {/* Status badges */}
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#DCFCE7] text-[#15803D] text-xs font-semibold">
                                <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
                                Verified
                            </span>
                            <OfferDuration endDate={data?.end_date} />
                        </div>

                        {/* Title */}
                        <h2 className="text-lg font-bold text-[#1E3A5F] leading-snug mb-6 line-clamp-3">
                            {data?.offer_title}
                        </h2>

                        {/* CTA */}
                        {data?.coupon_code ? (
                            <div className="bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] p-4">
                                <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider mb-2">
                                    Coupon Code
                                </p>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-white border-2 border-dashed border-[#E5E7EB] rounded-lg px-3 py-2.5 min-w-0">
                                        <span className="text-base font-mono font-bold text-[#1E3A5F] tracking-widest select-all truncate block">
                                            {data.coupon_code}
                                        </span>
                                    </div>
                                    <button
                                        onClick={handleCopy}
                                        className={`shrink-0 px-4 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                                            copied
                                                ? 'bg-[#F97316] text-white'
                                                : 'bg-[#F97316] text-white hover:bg-[#1E3A5F]'
                                        }`}
                                    >
                                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="w-4 h-4" />
                                        {copied ? "Copied!" : "Copy"}
                                    </button>
                                </div>
                                <p className="text-xs text-[#4B5563] mt-2 text-center">Apply at checkout</p>
                            </div>
                        ) : (
                            <Link
                                href={absoluteOutUrl}
                                target="_blank"
                                className="w-full py-3 rounded-xl bg-[#F97316] text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#1E3A5F] transition-all duration-300 shadow-md no-underline"
                                onClick={onClose}
                            >
                                <FontAwesomeIcon icon={faBagShopping} className="w-4 h-4" />
                                Get This Deal
                            </Link>
                        )}
                    </div>

                    {/* ── Offer Details ── */}
                    {cleanDesc && (
                        <div className="px-6 py-5 border-b border-[#E5E7EB]">
                            <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider mb-2">
                                Offer Details
                            </p>
                            <p className={`text-sm text-[#111827] leading-relaxed ${!showFullDesc && isLongDesc ? 'line-clamp-3' : ''}`}>
                                {cleanDesc}
                            </p>
                            {isLongDesc && (
                                <button
                                    onClick={() => setShowFullDesc(!showFullDesc)}
                                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F97316] hover:text-[#EA580C] transition-colors"
                                >
                                    {showFullDesc ? 'Show Less' : 'Read More'}
                                    <FontAwesomeIcon icon={showFullDesc ? faChevronUp : faChevronDown} className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="shrink-0 bg-[#F8FAFC] border-t border-[#E5E7EB] px-6 py-4 text-center">
                    <Link
                        href={merchantHref}
                        className="text-xs font-semibold text-[#4B5563] hover:text-[#1E3A5F] transition-colors no-underline"
                        onClick={onClose}
                    >
                        Explore more offers from this store
                    </Link>
                </div>
            </div>

            <style jsx>{`
                .hide-scroll::-webkit-scrollbar { width: 4px; }
                .hide-scroll::-webkit-scrollbar-track { background: transparent; }
                .hide-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 4px; }
                .hide-scroll::-webkit-scrollbar-thumb:hover { background: #1E3A5F; }
            `}</style>
        </div>,
        document.body
    );
};

export default SimpleOfferModal;
